import { useState, useEffect } from 'react';
import { serviceCategories } from '../../../mocks/servicesPage';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function ServicesGrid() {
  const { ref: headRef, isVisible: headVisible } = useScrollAnimation();
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation();
  const [visibleCards, setVisibleCards] = useState<boolean[]>(
    Array(serviceCategories.length).fill(false)
  );

  useEffect(() => {
    if (!gridVisible) return;
    serviceCategories.forEach((_, i) => {
      setTimeout(() => {
        setVisibleCards((prev) => {
          const next = [...prev];
          next[i] = true;
          return next;
        });
      }, 100 * i);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridVisible]);

  return (
    <section id="services-grid" className="py-16 lg:py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div
          ref={headRef as React.RefObject<HTMLDivElement>}
          className={`transition-all duration-700 ${headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="mb-6 flex items-center gap-3">
            <div className="w-8 h-0.5 bg-amber-500"></div>
            <span className="text-amber-600 text-sm font-semibold uppercase tracking-widest">
              What We Do
            </span>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12 lg:mb-16">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight max-w-2xl">
              Seven Pillars of<br />
              <span className="text-amber-500">Business Excellence</span>
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed max-w-md lg:text-right">
              Each service is designed to remove complexity, reduce risk, and accelerate your growth in Cyprus and beyond.
            </p>
          </div>
        </div>

        {/* Services Grid */}
        <div
          ref={gridRef as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
        >
          {serviceCategories.map((service, index) => (
            <Link
              key={service.id}
              to={`/services/${service.id}`}
              className={`group relative block rounded-2xl overflow-hidden hover:scale-[1.02] hover:shadow-2xl bg-slate-900 ${
                index === 6 ? 'sm:col-span-2 lg:col-span-1' : ''
              } ${visibleCards[index] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
              style={{
                height: '22rem',
                transitionProperty: 'opacity, transform, box-shadow',
                transitionDuration: '600ms',
                transitionTimingFunction: 'cubic-bezier(0.4,0,0.2,1)',
              }}
            >
              {/* Image */}
              <div className="absolute inset-0 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/10 group-hover:from-black/70 transition-all duration-300"></div>
              </div>

              {/* Index number */}
              <div className="absolute top-5 left-5 text-white/20 text-5xl font-bold leading-none select-none">
                {String(index + 1).padStart(2, '0')}
              </div>

              {/* Default Content */}
              <div className="relative h-full flex flex-col justify-end p-6 lg:p-8 group-hover:opacity-0 transition-opacity duration-300">
                <div className="flex items-end justify-between gap-4">
                  <h3 className="text-xl lg:text-2xl font-bold text-white leading-tight flex-1">
                    {service.title}
                  </h3>
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center border border-white/30 rounded-full">
                    <i className="ri-arrow-right-up-line text-white text-lg"></i>
                  </div>
                </div>
              </div>

              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-slate-900/97 opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col justify-center p-6 lg:p-8">
                <div className="w-10 h-0.5 bg-amber-500 mb-4"></div>
                <h3 className="text-xl lg:text-2xl font-bold text-white mb-3">{service.title}</h3>
                <p className="text-white/70 text-sm leading-relaxed mb-5 line-clamp-3">{service.description}</p>
                <ul className="space-y-1.5 mb-5">
                  {service.services.slice(0, 4).map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-white/60 text-sm">
                      <i className="ri-check-line text-amber-500 flex-shrink-0"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                  {service.services.length > 4 && (
                    <li className="text-amber-400 text-sm font-medium">
                      +{service.services.length - 4} more services
                    </li>
                  )}
                </ul>
                <span className="inline-flex items-center gap-2 text-amber-400 font-semibold text-sm group-hover:gap-3 transition-all">
                  <span>View Full Service</span>
                  <i className="ri-arrow-right-line"></i>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
